/* ==========================================================================
   FILE: modules/brain/components/PersonalitySettings.jsx
   IAMONEAI - Personality Settings Component
   5-axis personality model configuration
   ========================================================================== */

import React from 'react';
import { PersonalitySlider, SteppedSlider } from '../../../global/components';
import './PersonalitySettings.css';

// ==========================================================================
// PERSONALITY AXES
// ==========================================================================

const PERSONALITY_AXES = [
  {
    key: 'energy',
    label: 'Energy',
    lowLabel: 'Calm',
    highLabel: 'Energetic',
    description: 'How enthusiastic and animated my responses are',
    lowDesc: 'Measured, peaceful, relaxed tone',
    highDesc: 'Enthusiastic, lively, upbeat tone',
  },
  {
    key: 'formality',
    label: 'Formality',
    lowLabel: 'Casual',
    highLabel: 'Formal',
    description: 'How professional my communication style is',
    lowDesc: 'Friendly, relaxed language',
    highDesc: 'Professional, proper language',
  },
  {
    key: 'humor',
    label: 'Humor',
    lowLabel: 'Serious',
    highLabel: 'Playful',
    description: 'How often I use light humor and wit',
    lowDesc: 'Focused, straightforward',
    highDesc: 'Light-hearted, occasional jokes',
  },
  {
    key: 'directness',
    label: 'Directness',
    lowLabel: 'Gentle',
    highLabel: 'Direct',
    description: 'How straightforward my communication is',
    lowDesc: 'Tactful, softened delivery',
    highDesc: 'Clear, to-the-point delivery',
  },
  {
    key: 'empathy',
    label: 'Empathy',
    lowLabel: 'Neutral',
    highLabel: 'Empathetic',
    description: 'How emotionally attuned my responses are',
    lowDesc: 'Objective, factual focus',
    highDesc: 'Warm, understanding, caring',
  },
];

// ==========================================================================
// COMMUNICATION STYLE OPTIONS
// ==========================================================================

const LENGTH_OPTIONS = [
  { value: 'brief', label: 'Brief' },
  { value: 'concise', label: 'Concise' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'detailed', label: 'Detailed' },
];

const EMOJI_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'frequent', label: 'Frequent' },
];

const QUESTION_OPTIONS = [
  { value: 'minimal', label: 'Minimal' },
  { value: 'direct', label: 'Direct' },
  { value: 'curious', label: 'Curious' },
  { value: 'supportive', label: 'Supportive' },
];

// ==========================================================================
// PERSONALITY SETTINGS COMPONENT
// ==========================================================================

/**
 * Personality configuration with 5-axis sliders
 * 
 * @param {Object} props
 * @param {Object} personality - Current personality values (0-1)
 * @param {Object} communicationStyle - Current communication style
 * @param {Function} onUpdate - Handler for updates
 */
function PersonalitySettings({
  personality = {},
  communicationStyle = {},
  onUpdate,
  className = '',
}) {
  // Default values
  const defaults = {
    energy: 0.5,
    formality: 0.3,
    humor: 0.4,
    directness: 0.5,
    empathy: 0.7,
  };

  const handlePersonalityChange = (key, value) => {
    onUpdate?.(`personality.${key}`, value);
  };

  const handleStyleChange = (key, value) => {
    onUpdate?.(`communication_style.${key}`, value);
  };

  const settingsClasses = [
    'personality-settings',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={settingsClasses}>
      {/* Personality Section */}
      <div className="settings-section">
        <div className="settings-section-header">
          <h3 className="settings-section-title">Personality</h3>
          <p className="settings-section-desc">
            Adjust how I communicate with you
          </p>
        </div>

        <div className="personality-axes">
          {PERSONALITY_AXES.map((axis) => (
            <PersonalitySlider
              key={axis.key}
              label={axis.label}
              lowLabel={axis.lowLabel}
              highLabel={axis.highLabel}
              value={personality[axis.key] ?? defaults[axis.key]}
              onChange={(value) => handlePersonalityChange(axis.key, value)}
              description={axis.description}
            />
          ))}
        </div>

        {/* Personality Preview */}
        <PersonalityPreview personality={{ ...defaults, ...personality }} />
      </div>

      {/* Communication Style Section */}
      <div className="settings-section">
        <div className="settings-section-header">
          <h3 className="settings-section-title">Communication Style</h3>
          <p className="settings-section-desc">
            Fine-tune how I format my responses
          </p>
        </div>

        <div className="communication-style-options">
          <div className="style-option">
            <label className="style-option-label">Response Length</label>
            <p className="style-option-desc">How detailed should my responses be?</p>
            <SteppedSlider
              options={LENGTH_OPTIONS}
              value={communicationStyle.preferred_length || 'concise'}
              onChange={(value) => handleStyleChange('preferred_length', value)}
            />
          </div>

          <div className="style-option">
            <label className="style-option-label">Emoji Usage</label>
            <p className="style-option-desc">How often should I use emojis?</p>
            <SteppedSlider
              options={EMOJI_OPTIONS}
              value={communicationStyle.emoji_usage || 'minimal'}
              onChange={(value) => handleStyleChange('emoji_usage', value)}
            />
          </div>

          <div className="style-option">
            <label className="style-option-label">Question Style</label>
            <p className="style-option-desc">How should I ask follow-up questions?</p>
            <SteppedSlider
              options={QUESTION_OPTIONS}
              value={communicationStyle.question_style || 'curious'}
              onChange={(value) => handleStyleChange('question_style', value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// PERSONALITY PREVIEW
// ==========================================================================

/**
 * Shows a preview of how the AI will respond based on personality
 */
function PersonalityPreview({ personality }) {
  const preview = generatePreview(personality);

  return (
    <div className="personality-preview">
      <div className="personality-preview-header">
        <span className="personality-preview-label">Preview</span>
        <span className="personality-preview-hint">How I might respond to "How are you?"</span>
      </div>
      <div className="personality-preview-bubble">
        <div className="personality-preview-avatar">🧠</div>
        <p className="personality-preview-text">{preview}</p>
      </div>
    </div>
  );
}

/**
 * Generate preview text based on personality values
 */
function generatePreview(personality) {
  const { energy, formality, humor, directness, empathy } = personality;

  // Base responses
  const responses = {
    casual_warm: "Hey! I'm doing great, thanks for asking! 😊 How about you?",
    casual_neutral: "I'm good, thanks! What's on your mind?",
    formal_warm: "I'm well, thank you for asking. How may I assist you today?",
    formal_neutral: "I'm functioning well. How can I help you?",
    playful: "Living my best AI life! 🤖✨ What adventure are we tackling today?",
    empathetic: "I appreciate you checking in! I'm here and ready to help. How are you doing?",
    direct: "Good. What do you need?",
  };

  // Select based on personality
  if (formality > 0.7) {
    return empathy > 0.5 ? responses.formal_warm : responses.formal_neutral;
  }
  
  if (humor > 0.6) {
    return responses.playful;
  }
  
  if (directness > 0.7 && empathy < 0.4) {
    return responses.direct;
  }
  
  if (empathy > 0.6) {
    return responses.empathetic;
  }
  
  if (energy > 0.6) {
    return responses.casual_warm;
  }
  
  return responses.casual_neutral;
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default PersonalitySettings;